/**
 * "Run the Detective" — the guided tour DATA.
 *
 * The tour drives the LIVE application: every step navigates to a real route
 * with real filters and narrates what is on screen. There is no mock data and
 * no screenshot — the site itself is the detective.
 *
 * Kept free of React so the narrative is editable in one place and the step
 * transitions are unit-testable without a renderer.
 *
 * ROUTE PATHS ARE BASENAME-RELATIVE. The router mounts at `basename: '/app'`
 * (see `router.tsx`), so `navigate('/overview')` produces the browser URL
 * `/app/overview`. Writing `/app/overview` here would double the prefix and
 * land on the catch-all redirect.
 */

export interface DetectiveTourStep {
  /** Stable id — used as the React key and in tests. */
  id: string;
  /**
   * Basename-relative route to navigate to, or `null` to stay where the
   * previous step left the user (the closing step narrates over the page the
   * investigation ended on).
   */
  route: string | null;
  title: string;
  /** Rendered large at the left of the card — the step's visual anchor. */
  emoji: string;
  narration: string;
  /** Optional supporting points rendered as a short list under the narration. */
  bullets?: string[];
  /**
   * Optional one-click questions for the Ask AI panel. Each is verified to
   * ground in the RAG corpus — do not edit without re-verifying retrieval.
   */
  askChips?: string[];
  /**
   * Grace period (ms) before the narration card fades in, giving the page it
   * navigated to a moment to paint/fetch. Purely cosmetic — the tour never
   * blocks on it.
   */
  waitMs?: number;
}

/** The vehicle the closing act replays. */
export const DETECTIVE_TOUR_VEHICLE = 'BTN549B';

/**
 * The thirteen-beat story: fleet → the ML radar → the rules watching the rules
 * → the learned baseline → the scheme → its geography → the suspects → the one
 * vehicle → the investigator's desk → the merchant side → data trust → ask the
 * Detective → the closing claim.
 *
 * Figures quoted in the narration are live-verified against the demo dataset
 * and are deliberately verbatim — they are read aloud in the room.
 */
export const DETECTIVE_TOUR_STEPS: readonly DetectiveTourStep[] = [
  {
    id: 'fleet',
    route: '/overview?period=Q',
    title: 'The fleet',
    emoji: '🚛',
    narration:
      '176,725 vehicles. R1.69 billion in fuel this quarter. Somewhere in these numbers, money is leaking. Let’s find who — and let the machine do the looking.',
    waitMs: 400,
  },
  {
    id: 'radar',
    route: '/fraud-dashboard',
    title: 'The radar — where machine learning earns its keep',
    emoji: '🧠',
    narration:
      '592,000+ alerts across fuel, oil and toll. Behind them, the machine builds a DNA for EVERY vehicle from four reference points: the OEM benchmark (what the manufacturer says it should burn), the industry-standard benchmark for its class, the application it actually does — a grader on gravel is not a sedan on tar — and where it operates. Compared against each other, plus the vehicle’s own history and its peers, that DNA is its true operating cost. A transaction that breaks the DNA is a signal.',
    bullets: [
      'OEM benchmark × industry standard × application × location — four yardsticks, one DNA. Cost comparison and operating cost per vehicle fall straight out of it.',
      'A human investigator starts with a hunch — and hunches carry bias. The model scores every transaction with the same yardstick: a minister’s sedan and a works bakkie get identical scrutiny.',
      'Every vehicle is judged against ITS OWN DNA — its history, its class, its duty — never against an investigator’s suspicion. No favourites, no profiling, no fatigue: 25 million transactions, re-scored on every run.',
      'The severity trend you see here is evidence-ranked triage — worst first, by numbers, not by gut.',
    ],
    waitMs: 400,
  },
  {
    id: 'rule-health',
    route: '/rule-health',
    title: '74 rules, watching each other',
    emoji: '⚖️',
    narration:
      'Every rule’s firing rate is monitored like a patient’s vitals. A rule that goes quiet or goes wild is a data problem or a fraud wave — either way, someone gets told. This is how the machine stays honest.',
    waitMs: 400,
  },
  {
    id: 'baseline',
    route: '/consumption',
    title: 'The learned baseline, made visible',
    emoji: '📉',
    narration:
      'This is the ML layer you can see: every vehicle’s real L/100km against what the model expects from its own history and its peers. The drift table is pure mathematics — a NISSAN doing 16,000% over baseline isn’t an opinion.',
    waitMs: 500,
  },
  {
    id: 'scheme',
    route: '/fraud-map?preset=airtime-as-oil',
    title: 'The scheme: airtime as oil',
    emoji: '💳',
    narration:
      'Oil purchases in perfectly round amounts — R200.00, R500.00 on the dot. Real oil comes in odd amounts; airtime doesn’t. 44,569 flagged purchases. Every dot is a station.',
    waitMs: 600,
  },
  {
    id: 'hotspots',
    route: '/fraud-map?preset=airtime-as-oil&view=hotspots',
    title: 'Where it concentrates',
    emoji: '📍',
    narration:
      'It’s not random. These stations top the list — this is where you send an inspector.',
    waitMs: 600,
  },
  {
    id: 'suspects',
    route: '/offenders',
    title: 'The suspects',
    emoji: '🎯',
    narration:
      '39,572 flagged vehicles, ranked worst-first by evidence. No investigator chose this order — the data did.',
    waitMs: 500,
  },
  {
    id: 'suspect-vehicle',
    route: `/maps?vehicle=${DETECTIVE_TOUR_VEHICLE}&replay=1&autoplay=1`,
    title: 'The suspect: BTN549B',
    emoji: '🚨',
    narration:
      'A Chevrolet Utility. Watch its trail — every coloured blip is a violation at a real station: 76 flagged stops, 51 of them round-value oil. This is what you put in front of a prosecutor.',
    waitMs: 900,
  },
  {
    id: 'desk',
    route: '/fraud-hub',
    title: 'The investigator’s desk',
    emoji: '🗂️',
    narration:
      'Critical cases, severity trends, top offenders by vehicle, driver, card, attendant, station or merchant — the day’s triage, machine-ordered.',
    waitMs: 500,
  },
  {
    id: 'merchants',
    route: '/merchant-network',
    title: 'The other side of the till',
    emoji: '🏪',
    narration:
      'Fraud takes two. The same lens ranks stations and merchants — dormant sites with thousands of oil sales don’t hide here.',
    waitMs: 500,
  },
  {
    id: 'trust',
    route: '/data-quality',
    title: 'Trust, measured',
    emoji: '✅',
    narration:
      'Every claim depends on data you can defend, so the platform grades its own inputs. Weak data lowers confidence automatically — the system would rather admit doubt than manufacture certainty.',
    waitMs: 400,
  },
  {
    id: 'ask',
    route: '/fraud',
    title: 'Ask the Detective',
    emoji: '🕵️',
    narration:
      'Every number you’ve seen cites a real record. Now interrogate it yourself — the Detective answers from evidence, or refuses.',
    askChips: [
      'What does rule OIL-1 detect?',
      'Why does a round oil amount matter?',
      'What does Tank Overflow mean?',
    ],
    waitMs: 500,
  },
  {
    id: 'close',
    route: null,
    title: 'No bias. No fatigue. No unsourced claims.',
    emoji: '🏁',
    narration:
      'Five detection layers, one immutable audit trail, zero unsourced accusations. The machine takes human bias out of the hunt — and hands humans the proof.',
  },
] as const;

export const DETECTIVE_TOUR_LENGTH = DETECTIVE_TOUR_STEPS.length;
